import { useEffect, useRef } from "react";
import { usePartnerRequestDetailsStore } from "../../../../stores/Cac/Partner/partnerRequestDetails";
import {
  downloadChat,
  getCommentaries,
  postCommentary,
} from "../../../../services/Api/requestService";
import send from "../../../../assets/icons/sendWhite.svg";
import { usePartnerRequestsStore2 } from "../../../../stores/Cac/Partner/partnerRequests";
import {
  dateFormat,
  hourFormat,
} from "../../../../services/Utils/functionService";
import { showToastError } from "../../../../services/Toasts/toasts";
import download from "../../../../assets/icons/download-white.svg";

const RequestChat = () => {
  // Stores
  const { requestId, updateRequest, setUpdateRequest, missionData } =
    usePartnerRequestsStore2();
  const { messages, setMessages, message, setMessage } =
    usePartnerRequestDetailsStore();

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (requestId !== 0)
      getCommentaries(Number(requestId))
        .then((response) => {
          setMessages(response);
        })
        .catch(() => showToastError("Une erreur s'est produite"));
  }, [updateRequest]);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      if (textareaRef.current.scrollHeight > 120) {
        textareaRef.current.style.height = "120px";
        textareaRef.current.style.overflowY = "auto";
      } else {
        textareaRef.current.style.overflowY = "hidden";
      }
    }
  };

  const handlePostComment = () => {
    postCommentary(Number(requestId), message)
      .then(() => {
        setMessage("");
        setUpdateRequest(!updateRequest);
      })
      .catch(() => showToastError("Une erreur s'est produite"));
  };

  const scrollToBottomAutomatic = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottomAutomatic();
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handlePostComment();
    } else if (e.key === "Enter" && e.shiftKey) {
      setMessage(message + "\n");
    }
  };

  // Style des messages
  const getMessageStyle = (comment: any) => {
    if (comment?.isRefusal) {
      return "bg-gradient-to-r from-red-500 to-red-600 text-white border-red-400";
    }

    if (comment?.isReminder) {
      return "bg-gray-400 to-gray-500 text-white border-gray-400 mx-auto";
    }
    switch (comment?.profil) {
      case "Partner":
        return "bg-light-lavender text-gray-800 border-blue-500 ml-auto";
      case "Customer":
        return "bg-dark-lavender2 text-gray-800 border-gray-300";
      case "Deleted":
      case "":
        return "bg-gray-400 to-gray-500 text-white border-gray-400 mx-auto";
      default:
        return "gray-200 to-gray-300 text-gray-800 border-gray-400";
    }
  };

  // Alignement des messages en fonction du profil
  const getMessageAlignment = (comment: any) => {
    if (comment?.isRefusal) return "justify-center";
    if (comment?.isReminder) return "justify-center";
    if (comment?.profil === "Partner") return "justify-end";
    if (comment?.profil === "Customer") return "justify-start";
    if (comment?.profil === "Deleted" || comment?.profil === "")
      return "justify-center";
    return "justify-start";
  };

  // Fonction pour télécharger le chat
  const handleDownload = () => {
    downloadChat(Number(requestId))
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `chat-${requestId}.pdf`;
        a.click();
      })
      .catch((error) => {
        console.log(error);
        showToastError("Une erreur s'est produite");
      });
  };  

  return (
    <div className="mx-auto flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-lg">
      {/* Header du chat */}
      <div className="flex flex-shrink-0 items-center justify-between bg-gradient-to-r bg-light-lavender px-3 py-2 text-gray-700 sm:px-6">
        <div className="flex flex-col items-start justify-start">
          <h3 className="font-BarlowCondensed text-lg font-semibold sm:text-xl">
            Discussion
          </h3>
          <p className="mt-1 hidden text-sm sm:block">
            Échangez avec votre interlocuteur sur cette requête
          </p>
        </div>
        <button onClick={handleDownload} className="cursor-pointer rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 p-2.5 text-white transition-all duration-200 hover:from-blue-700 hover:to-blue-800 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 sm:p-3 sm:hover:scale-105">
          <img src={download} alt="Télécharger" className="w-5 h-5" />
        </button>
      </div>

      {/* Zone des messages */}
      <div className="min-h-0 flex-1 overflow-hidden p-2 sm:p-4">
        {messages?.length > 0 ? (
          <div
            className="custom-scrollbar h-full space-y-3 overflow-y-auto pr-1 sm:space-y-4 sm:pr-2"
            ref={scrollRef}
          >
            {messages?.map((comment: any, index: number) => (
              <div
                key={index}
                className={`flex ${getMessageAlignment(comment)}`}
              >
                <div
                  className={`max-w-[90%] rounded-2xl border px-3 py-2 shadow-sm sm:max-w-[75%] sm:px-4 sm:py-3 ${getMessageStyle(comment)} transition-all duration-200 hover:shadow-md`}
                >
                  {/* En-tête du message */}
                  {!comment?.isRefusal && (
                    <div className="flex items-center justify-between mb-2 ">
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-sm mr-2">
                          {comment?.user}
                        </span>
                      </div>
                      <div className="text-xs opacity-80">
                        {dateFormat(comment?.createdAt) +
                          " " +
                          hourFormat(comment?.createdAt)}
                      </div>
                    </div>
                  )}

                  {/* Contenu du message */}
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">
                    {comment?.text}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Aucun message
            </h3>
            <p className="text-gray-500 text-sm max-w-xs">
              Commencez la conversation en envoyant votre premier message
            </p>
          </div>
        )}
      </div>

      {/* Zone de saisie */}
      <div className="flex-shrink-0 border-t border-slate-200 bg-white p-2 sm:p-4">
        <div className="flex items-end space-x-2 sm:space-x-3">
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              disabled={missionData?.closed === 1}
              className="w-full resize-none rounded-xl border border-slate-300 bg-slate-50 p-3 text-gray-800 transition-all duration-200 placeholder-gray-500 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 sm:pr-12"
              placeholder="Tapez votre message..."
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                adjustTextareaHeight();
              }}
              onKeyDown={(e) => handleKeyDown(e)}
              style={{ resize: "none" }}
              rows={1}
            />
            <div className="absolute bottom-3 right-3 hidden text-xs text-gray-400 sm:block">
              Shift + Entrée pour une nouvelle ligne
            </div>
          </div>

          <button
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl p-3 transition-all duration-200 transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            onClick={handlePostComment}
            disabled={!message.trim()}
          >
            <img src={send} alt="Envoyer" className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Styles CSS personnalisés pour la scrollbar */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default RequestChat;
